import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import * as dayjs from 'dayjs'
import { getSelectedPlot } from '../../common';
import { useAlert } from 'react-alert'
import AppContext from '../../AppContext';
import { ObservationCategory } from '../../Constants/SharedMapFeature';
import { FaMapMarkerAlt } from "react-icons/fa";
import { FaBug } from "react-icons/fa6";
import { IoMdCalendar } from "react-icons/io";
import { retrieveWgsCoordinates } from '../../Map/commonMapFunctions';
import ImageShowModal from '../Modals/ImageShowModal';
import { TbPlantOff, TbCategory } from "react-icons/tb";
import { RiVirusLine } from "react-icons/ri";

import WKT from 'ol/format/WKT';
import { transform } from 'ol/proj';

import '../../css/general.css'
import '../../css/mobiNewStyles.css'

const FeatureInfoPane = (props) => {
    const { t } = useTranslation();
    const { auth } = useContext(AppContext);

    const [coordinates, setCoordinates] = useState(null);
    const [featureData, setFeatureData] = useState(null);
    const [imageShow, setImageShow] = useState(false);
    const [imageToView, setImageToView] = useState(null);
    const [infoExpanded, setInfoExpanded] = useState(false);
    const [infoOverflows, setInfoOverflows] = useState(false);
    const infoRef = useRef(null);

    const observationCategories = [
        { value: ObservationCategory.Weeds, label: t('Weeds') },
        { value: ObservationCategory.Diseases, label: t('Diseases') },
        { value: ObservationCategory.Pests, label: t('Pests') },
        { value: ObservationCategory.Other, label: t('Other') }
    ];

    const selectedPlot = getSelectedPlot();
    const alert = useAlert();

    useEffect(() => {
        if (props.feature) {
            const format = new WKT();
            const geometry = format.readGeometry(props.feature.wktGeometry);

            const [x, y] = geometry.getCoordinates();

            const [lon, lat] = transform(
                [x, y],
                'EPSG:3067',
                'EPSG:4326'
            );

            const lonFixed = lon.toFixed(3);
            const latFixed = lat.toFixed(3);
            setFeatureData({
                SharedFeatureId: props.feature.sharedFeatureId,
                CategoryId: props.feature.categoryId,
                ObservationId: props.feature.observationId,
                Unit: props.feature.unit,
                ExtraInfo: props.feature.extraInfo,
                SharedDate: props.feature.sharedDate,
                Amount: props.feature.amount,
                Visibility: props.feature.visibility,
                Coordinates: {
                    longitude: x, latitude: y, wgs: { longitude: lonFixed, latitude: latFixed } }
            });
            setCoordinates({ longitude: lonFixed, latitude: latFixed });
            return;
        }
        if (!props.data) return;
        const coords = retrieveWgsCoordinates(props.data.coordinates);
        setCoordinates(coords);
        setFeatureData(props.data.feature.values_);
    }, [props.data, props.feature]);

    useEffect(() => {
        // Reset when underlying feature changes and re-measure overflow.
        setInfoExpanded(false);
        if (!infoRef.current) {
            setInfoOverflows(false);
            return;
        }
        // Compare scrollHeight to clientHeight while the element is in
        // its clamped state to determine whether the toggle is needed.
        const el = infoRef.current;
        setInfoOverflows(el.scrollHeight > el.clientHeight + 1);
    }, [featureData]);

    const getObservationTypeName = (objectMethod) => {
        if (!props.resources || !props.resources.observationTypes) return objectMethod;
        const obs = props.resources.observationTypes.find(o => o.value === objectMethod);
        return obs ? obs.label : objectMethod;
    };

    const getObservationMeasureName = (unit) => {
        if (!props.resources || !props.resources.observationMeasures) return unit;
        const measure = props.resources.observationMeasures.find(m => m.value.toString() === unit);
        return measure ? measure.label : unit;
    };

    const getCategoryName = (category) => {
        const cat = observationCategories.find(c => c.value === category);
        return cat ? cat.label : category;
    };

    const GetObservationIcon = (type) => {
        if (props.images && props.images.length > 0) {
            return <img src={props.images[0].thumbnailImage ? `${props.images[0].thumbnailImage}` : URL.createObjectURL(props.images[0])} alt={props.images[0].name} onClick={() => {
                setImageToView(props.images[0]);
            }} />;
        }
        if (props.imagesLoading) {
            return <Spinner animation="border" variant="mmfi-new" size="sm" />;
        }

        if (type === 1) return <TbPlantOff className="filter-icon" fontSize={32} />;
        if (type === 2) return <RiVirusLine className="filter-icon" fontSize={40} />;
        if (type === 4) return <TbCategory className="filter-icon" fontSize={32} />;
        return <FaBug className="filter-icon" fontSize={32} />;
    }

    const paneOnClick = (event) => {
        if (props.onClick) {
            props.onClick(featureData);
        }
    }

    return (
        <>
            <Card
                className={`${props.className ? ` ${props.className}` : ''}`}
                onClick={() => { paneOnClick(); }}>
                {featureData ?
                    <>
                        <div className="sharing-popup-img text-mmfi-new" onClick={() => {
                            if (props.images.length) {
                                setImageShow(true);
                            }
                        }}>{GetObservationIcon(featureData.CategoryId)}</div>
                        <div className="sharing-popup-category">{getCategoryName(featureData.CategoryId)}</div>
                        <div className="sharing-popup-observation text-mmfi-new">{getObservationTypeName(featureData.ObservationId)}, {featureData.Amount > 0 && featureData.Amount} {getObservationMeasureName(featureData.Unit)}</div>
                        {featureData.ExtraInfo ?
                            <div className="sharing-popup-info-wrapper">
                                <div
                                    ref={infoRef}
                                    className={`sharing-popup-info text-mmfi-new${infoExpanded ? ' expanded' : ''}`}
                                >
                                    {featureData.ExtraInfo}
                                </div>
                                {props.small && !infoExpanded && infoOverflows ?
                                    <button
                                        type="button"
                                        className="sharing-popup-info-toggle"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setInfoExpanded(prev => !prev);
                                        }}
                                    >
                                        {infoExpanded ? t('ShowLess') : t('ShowMore')}
                                    </button>
                                    : null}
                            </div>
                            : null}
                        {coordinates ?
                            <span className="sharing-popup-footer">
                                <span>
                                    <FaMapMarkerAlt className="button-icon" color={'#198754'} fontSize={16} />
                                    {coordinates.longitude}, {coordinates.latitude}
                                </span>
                                <span style={{ marginLeft: 'auto' }}>
                                    <IoMdCalendar className="button-icon" color={'gray'} fontSize={16} />
                                    {dayjs(featureData.SharedDate).format('DD.MM.YYYY')}
                                </span>
                            </span>
                            :
                            <span className="sharing-popup-footer align-right">
                                <IoMdCalendar className="button-icon" color={'gray'} fontSize={16} />
                                <span>{dayjs(featureData.SharedDate).format('DD.MM.YYYY')}</span>
                            </span>
                        }
                    </>
                    : ''}
            </Card>
            <ImageShowModal
                show={imageShow}
                onHideImage={() => {
                    setImageShow(false);
                }}
                image={imageToView}
            />
        </>
    );
}

export default FeatureInfoPane;